import { LINHA_DO_DRE, type Destino, type LinhaDoDre, type LinhaLida, type LinhaParaImportar, type TipoFinanceiro } from "@bookdental/schemas";
import type { CategoriaFinanceira } from "@/lib/dinheiro";

/** O destino como valor de <select>: "ignorar", "cat:<id>" ou "nova:<tipo>:<linha>". */
export const destinoEmTexto = (d: Destino) => (d.acao === "ignorar" ? "ignorar" : d.acao === "categoria" ? `cat:${d.id}` : `nova:${d.tipo}:${d.linha}`);
export function textoEmDestino(v: string): Destino {
  if (v.startsWith("cat:")) return { acao: "categoria", id: v.slice(4) };
  if (v.startsWith("nova:")) {
    const [, tipo, linha] = v.split(":");
    return { acao: "criar", tipo: tipo as TipoFinanceiro, linha: linha as LinhaDoDre };
  }
  return { acao: "ignorar" };
}

export type GrupoDoRotulo = { rotulo: string; quantidade: number; totalCents: number };

/** Os rótulos distintos da planilha (conta ou categoria), com quantas linhas e quanto somam. */
export function gruposDosRotulos(lidas: LinhaLida[]): GrupoDoRotulo[] {
  const mapa = new Map<string, GrupoDoRotulo>();
  for (const l of lidas) {
    const g = mapa.get(l.rotulo) ?? { rotulo: l.rotulo, quantidade: 0, totalCents: 0 };
    g.quantidade += 1;
    g.totalCents += Math.abs(l.valorCents ?? 0);
    mapa.set(l.rotulo, g);
  }
  return [...mapa.values()].sort((a, b) => b.totalCents - a.totalCents);
}

export type SituacaoDaLinha = "ok" | "ignorada" | "invalida" | "mesFechado" | "repetida" | "producao";
export type LinhaMontada = { lida: LinhaLida; situacao: SituacaoDaLinha; tipo: TipoFinanceiro | null; linha: LinhaDoDre | null; destino: Destino };

/** Cada linha com o destino escolhido e o que impede de importar (a prévia do servidor completa). */
export function montarLinhas(
  lidas: LinhaLida[],
  destinos: Record<string, Destino>,
  categorias: CategoriaFinanceira[],
  previa?: { fechados: string[]; faturamento: Record<string, number>; repetidos: number[] } | null,
): LinhaMontada[] {
  const porId = new Map(categorias.map((c) => [c.id, c]));
  let candidato = -1;
  return lidas.map((lida) => {
    const destino = destinos[lida.rotulo] ?? { acao: "ignorar" };
    const cat = destino.acao === "categoria" ? porId.get(destino.id) : null;
    const tipo = destino.acao === "criar" ? destino.tipo : (cat?.tipo ?? null);
    const linha = destino.acao === "criar" ? destino.linha : (cat?.linhaDoDre ?? null);
    const base = { lida, tipo, linha, destino };
    if (destino.acao === "ignorar") return { ...base, situacao: "ignorada" as const };
    if (!lida.competencia || !lida.valorCents) return { ...base, situacao: "invalida" as const };
    // Mesmo critério de `candidatosDa`: o índice tem que bater com o da prévia.
    candidato += 1;
    if (!tipo) return { ...base, situacao: "invalida" as const };
    if (previa?.fechados.includes(lida.competencia)) return { ...base, situacao: "mesFechado" as const };
    if (previa?.repetidos.includes(candidato)) return { ...base, situacao: "repetida" as const };
    if (tipo === "ENTRADA" && linha === "RECEITA_OPERACIONAL" && (previa?.faturamento[lida.competencia] ?? 0) > 0) return { ...base, situacao: "producao" as const };
    return { ...base, situacao: "ok" as const };
  });
}

/** As linhas válidas como candidatas para a prévia (mesma ordem do índice usado acima). */
export const candidatosDa = (lidas: LinhaLida[], destinos: Record<string, Destino>) =>
  lidas
    .filter((l) => (destinos[l.rotulo]?.acao ?? "ignorar") !== "ignorar" && l.competencia && l.valorCents)
    .map((l) => ({ competencia: l.competencia!, valorCents: l.valorCents!, descricao: l.descricao }));

export function paraImportar(m: LinhaMontada, categoriaId: string): LinhaParaImportar {
  return {
    competencia: m.lida.competencia!,
    descricao: m.lida.descricao,
    valorCents: Math.abs(m.lida.valorCents!),
    categoriaId,
    pagoEm: m.lida.pagoEm,
    formaDePagamento: m.lida.forma as LinhaParaImportar["formaDePagamento"],
    parceiro: m.lida.parceiro,
  };
}

export const rotuloDaLinha = (l: LinhaDoDre | null) => (l ? LINHA_DO_DRE[l].rotulo : "Sem linha do DRE");
